import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useRef, useState } from 'react';
import { RiDvdLine } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import styles from './common.module.scss';

import actions from '@/actions';
import ConfirmSlider from '@/components/ui/ConfirmSlider';
import { FieldNumber } from '@/components/ui/kit/Field';
import LabelGroup from '@/components/ui/LabelGroup';
import useActionCommand from '@/hooks/command/mavlink/useActionCommand';
import { NotifierService as notifier } from '@/libs/Notifier';
import { executeMavLinkCommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const Panel = ({ data: robots }) => {
  const dispatch = useDispatch();
  const [isProcessing, setProcessing] = useState(false);
  const inputRefs = useRef([
    { number: useRef(), pwm: useRef() },
    { number: useRef(), pwm: useRef() },
  ]).current;
  const actionCommand = useActionCommand();

  const handleBlur = (e) => {
    const [key, index] = e.target.name.split('.');

    inputRefs[index][key].current.value = Math.trunc(e.target.value);
  };

  //#region Process Functions
  const process = async (robot) => {
    const inputs = inputRefs.map((input) => ({
      number: Number(input.number.current.value),
      pwm: Number(input.pwm.current.value),
    }));

    try {
      await executeMavLinkCommand(robot, actionCommand.setServo(inputs[0].number, inputs[0].pwm));
      await executeMavLinkCommand(robot, actionCommand.setServo(inputs[1].number, inputs[1].pwm));

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };
  //#endregion

  //#region Result Handlers
  const onSuccess = (robot) => {
    notifier.success(`Servo set successfully on ${robot.name}.`);
    setProcessing(false);
  };

  const onFailure = (robot) => {
    notifier.error(`Failed to set servo on ${robot.name}.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) {
      dispatch(actions.command.resetActivePanels());
      return;
    }

    robots.forEach((robot) => process(robot));
  }, [isProcessing]);

  const doCommand = () => {
    setProcessing(true);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('desc')}>Sets PWM output value for specified servo instances</div>
      <div className={cx('form')}>
        <LabelGroup label="Instance Number">
          <div className={cx('fieldWrapper')}>
            <FieldNumber ref={inputRefs[0].number} name="number.0" defaultValue={9} onBlur={handleBlur} />
            <FieldNumber ref={inputRefs[1].number} name="number.1" defaultValue={10} onBlur={handleBlur} />
          </div>
        </LabelGroup>
        <LabelGroup label="PWM">
          <div className={cx('fieldWrapper')}>
            <FieldNumber ref={inputRefs[0].pwm} name="pwm.0" defaultValue={1800} onBlur={handleBlur} />
            <FieldNumber ref={inputRefs[1].pwm} name="pwm.1" defaultValue={1800} onBlur={handleBlur} />
          </div>
        </LabelGroup>
      </div>
      <ConfirmSlider
        width="100%"
        height={40}
        onConfirm={doCommand}
        isProcessing={isProcessing}
        labelStyle={{ fontSize: 12 }}
      />
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Set Servo';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = false;
Command.commanderOnly = true;
Command.Icon = RiDvdLine;
Command.Panel = Panel;

export default Command;
